import { useState } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import toast from 'react-hot-toast';
import { useOutletContext } from 'react-router-dom';
import { StepShell } from 'components/Profile';
import { ProfileActions } from 'store/actions';
import Intro from './Intro';
import Question from './Question';
import NoInsurance, { Continue, NoInsuranceHeader } from './NoInsurance';
import MemberInsurance, { InsuranceNeedsCancelling } from './MemberInsurance';
import Membership from './Membership';
import StepFrame from './Steps/Frame';
import Method from './Steps/Method';
import CardPhotos from './Steps/CardPhotos';
import PlanDetails from './Steps/PlanDetails';
import PolicyHolder from './Steps/PolicyHolder';
import Review from './Steps/Review';
import { DEFAULT_FLOW, INSURANCE_FLOWS, formatPayload, getCarrierOptions, getInitialValues, locateStep } from './helper';
import { getPatient } from 'library/auth';
import { PAYMENT_LOCKED_HINT } from 'pages/Profile/helper';

const enhancer = connect(null, { ...ProfileActions });

const Form = ({ updateInsurance, insurance = null, practice = null, onSaved = null, inModal = false,
                onCancel = null, addInsurance = false }) => {
  const { sectionData, mergeSection, goToNextStep, paymentLocked }: any = useOutletContext() || {};
  const initialData = insurance ?? sectionData?.insurance;
  const carrierOptions = getCarrierOptions(practice ?? sectionData?.practice);

  // Opened FROM "add insurance": skip every choice and go straight to the form,
  // because that click was the choice.
  const [started, setStarted] = useState(addInsurance);
  // Only asked while nothing is on file — once they have answered, the record
  // itself says which way, and Intro's own copy covers changing their mind.
  // State, not derived: a patient already marked self-pay has answered "no", but
  // the back arrow has to be able to UNanswer it. Deriving this from the record
  // left that arrow with nothing to change.
  const [chose, setChose] = useState(initialData?.active === false);
  // Reported by the membership panel once it knows; all null until then.
  const [{ offering = null, member = null, enrolled = null, manageable = true, subscriberName = null },
         setStatus] = useState<any>({});

  const insured = initialData?.active === true;
  const [flow, setFlow] = useState(DEFAULT_FLOW);
  const [stepIndex, setStepIndex] = useState(0);

  const steps = INSURANCE_FLOWS[flow];
  const stepId = steps[stepIndex];
  const isLastStep = stepIndex === steps.length - 1;

  const showIntro = () => {
    setStarted(false);
    setChose(false);
  };
  const exitWizard = onCancel ?? showIntro;

  const goTo = (index, nextFlow = flow) => {
    setFlow(nextFlow);
    setStepIndex(index);
  };

  const goBack = () => (stepIndex === 0 ? showIntro() : goTo(stepIndex - 1));

  const editStep = (targetId) => {
    const target = locateStep(targetId, flow);
    goTo(target.index, target.flow);
  };

  const save = async (values) => {
    try {
      const { data } = await updateInsurance(formatPayload(values));

      toast.success(values.active ? 'Insurance data saved successfully' : "You're marked as self-pay");

      if (onSaved)
        onSaved(data);
      else {
        mergeSection('insurance', data);
        goToNextStep('insurance');
      }

    } catch (error) {
      toast.error(error.errors || 'Failed to save insurance data. Please try again later.');
    }
  };

  const handleSubmit = async (values) => {
    if (!started || isLastStep) return save(values);

    setStepIndex(stepIndex + 1);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={getInitialValues(initialData, carrierOptions)}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, submitForm, isSubmitting }) => {
        const startWizard = () => {
          setFieldValue('active', true);
          setStarted(true);
        };

        const markSelfPay = async () => {
          await setFieldValue('active', false);
          submitForm();
        };

        // Until the membership panel reports back we don't know whether this
        // patient already has one, and every state below is a different answer
        // to that. Nothing is drawn around it in the meantime.
        const waiting = !inModal && enrolled === null;
        // A member's membership IS their answer — nothing is asked over the top
        // of it. Someone with insurance on file keeps their own card, and the
        // panel below offers to switch.
        const header = waiting || enrolled ? null : insured ? (
          <>
            <Intro
              attributes={initialData}
              onAdd={startWizard}
              onSelfPay={markSelfPay}
              onCancel={onCancel}
              isSubmitting={isSubmitting}
              disabled={paymentLocked}
            />

            {/* This step hides the step footer, so the hint has nowhere else to live. */}
            {paymentLocked && (
              <p className="mt-3 text-center text-[12.5px] font-medium text-secondary-400">
                {PAYMENT_LOCKED_HINT}
              </p>
            )}
          </>
        ) : chose ? (
          <NoInsuranceHeader onBack={showIntro} disabled={isSubmitting || paymentLocked} />
        ) : (
          <Question
            onAnswer={(answer) => (answer === 'yes' ? startWizard() : setChose(true))}
            onCancel={onCancel}
            disabled={paymentLocked}
          />
        );

        // No plans in the appointment modal, so nothing there for the label to be
        // "without"; null elsewhere until they report, because guessing one and
        // settling on the other changes the text under the patient's finger.
        const continueLabel = inModal
          ? 'Continue without insurance'
          : member === null
            ? null
            : `Continue with${member ? ' my membership' : 'out a membership'}`;

        // A member's options hang below their membership rather than above it:
        // adding insurance, or looking at the one they already added while the
        // practice decides. Everyone else leaves the step through NoInsurance,
        // which is what records them as self-pay.
        const footer = waiting ? null : enrolled ? (
          <div className="mt-5 space-y-4">
            {/* Only a paid membership gets this. An unfinished checkout covers
                nobody, so there is nothing yet for insurance to be an
                alternative to. */}
            {!member ? null : insured ? (
              <MemberInsurance
                attributes={initialData}
                memberName={getPatient()?.full_name}
                onUpdate={startWizard}
                disabled={paymentLocked}
                manageable={manageable}
                subscriberName={subscriberName}
              />
            ) : (
              <InsuranceNeedsCancelling manageable={manageable} subscriberName={subscriberName} />
            )}

            <Continue
              label="Continue"
              onClick={insured ? () => goToNextStep?.('insurance') : markSelfPay}
              isSubmitting={isSubmitting}
              disabled={paymentLocked}
            />
          </div>
        ) : insured || !chose ? null : (
          <NoInsurance
            onContinue={markSelfPay}
            offering={inModal ? false : offering}
            label={continueLabel}
            isSubmitting={isSubmitting}
            disabled={paymentLocked}
          />
        );

        return (
          <StepShell stepId="insurance" inModal={inModal} hideFooter>
            {started ? (
              <StepFrame
                stepId={stepId}
                current={stepIndex + 1}
                total={steps.length}
                onBack={goBack}
                onCancel={exitWizard}
                footer={stepId !== 'method'}
                submitLabel={isLastStep ? 'Save insurance' : 'Continue'}
                isSubmitting={isSubmitting}
              >
                {stepId === 'method' && <Method onChoose={(nextFlow) => goTo(1, nextFlow)} />}
                {stepId === 'card' && <CardPhotos onTypeInstead={() => goTo(1, 'typed')} />}
                {stepId === 'details' && <PlanDetails carrierOptions={carrierOptions} />}
                {stepId === 'holder' && (
                  <PolicyHolder
                    policyHolder={values.policy_holder}
                    onChange={(holder) => setFieldValue('policy_holder', holder)}
                  />
                )}
                {stepId === 'review' && <Review values={values} carrierOptions={carrierOptions} onEdit={editStep} />}
              </StepFrame>
            ) : (
              // One slot for the membership panel, with the chrome around it
              // changing — moving it between parents would unmount it, refetch,
              // and flash a plan grid at someone who is already a member.
              <>
                {header}

                {!inModal && (
                  <div className={header ? 'mt-5' : ''}>
                    <Membership
                      onStatus={setStatus}
                      onAddInsurance={startWizard}
                      insured={insured}
                      // Nothing is pitched under the question itself — the panel
                      // is mounted only so it can report an existing membership.
                      pitch={insured || chose}
                    />
                  </div>
                )}

                {footer}
              </>
            )}
          </StepShell>
        );
      }}
    </Formik>
  );
};

export default enhancer(Form);
